import { t } from '@lingui/core/macro';
import { LoadingIndicator, MeetingsBuyer, PostBookingDto, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    confirmMeetingForBuyer,
    deleteBooking,
    getAvailableTimesServices,
    getBookings,
    getNextFreeTimesServices,
    getServices,
    postBooking,
} from '@/services/booking';
import { loadBuyerMeetings } from '@/state/buyer/meetings/creator';
import { IState } from '@/state/IState';
import { useAppDispatch, useAsyncState, useImpersonationInfo } from '@/utils/hooks';
import BuyerPageTitle from '../BuyerPageTitle/BuyerPageTitle';

interface BuyerMeetingsProps {}

const BuyerMeetings: React.FC<BuyerMeetingsProps> = (props) => {
    const dispatch = useAppDispatch();
    const residentialUnitId = useSelector((state: IState) => state.buyer.permissions!.residentialUnitId);
    const internalMeetings = useSelector((state: IState) => state.buyer.meetings);
    const isInViewAsBuyerMode = useImpersonationInfo();
    const [externalMeetings, , reloadExternalMeetings] = useAsyncState(
        async () => (residentialUnitId ? (isInViewAsBuyerMode ? [] : await getBookings(residentialUnitId)) : null),
        [residentialUnitId],
        null
    );

    const meetings = useMemo(() => {
        if (internalMeetings && externalMeetings) {
            const combinedMeetings = [...internalMeetings, ...externalMeetings];
            const beforeToday = combinedMeetings.filter((x) => new Date(x.from) < new Date());
            const afterToday = combinedMeetings.filter((x) => new Date(x.from) >= new Date());
            return [...afterToday, ...beforeToday];
        }
        return null;
    }, [externalMeetings, internalMeetings]);

    const [services] = useAsyncState(
        async () => (residentialUnitId ? (isInViewAsBuyerMode ? [] : await getServices(residentialUnitId)) : null),
        [residentialUnitId],
        null
    );
    if (!meetings || !services || !residentialUnitId) return <LoadingIndicator />;
    const deleteMeeting = async (meetingId: number) => {
        if (!residentialUnitId) return;
        await deleteBooking(residentialUnitId, meetingId);
        await reloadExternalMeetings();
    };
    const getTimes = (serviceId: number, from: Date, to: Date) => {
        return getAvailableTimesServices(residentialUnitId, serviceId, from, to);
    };
    const getNextFreeTimes = (serviceId: number, from: Date) => {
        return getNextFreeTimesServices(residentialUnitId, serviceId, from);
    };
    const doBookMeeting = async (id: number, data: PostBookingDto) => {
        await postBooking(residentialUnitId, id, data);
        await reloadExternalMeetings();
    };
    const doConfirm = async (id: number) => {
        const meeting = meetings.find((m) => m.id === id);
        if (meeting && meeting.confirmToken) {
            await confirmMeetingForBuyer(meeting.confirmToken).catch((error) => {
                if (error.status === 403) {
                    Toast.error(t`You can't confirm anymore.`);
                    dispatch(loadBuyerMeetings());
                }
            });
            await dispatch(loadBuyerMeetings());
        }
    };

    return (
        <div style={{ marginTop: 24 }}>
            <BuyerPageTitle />
            <MeetingsBuyer
                isInViewAsBuyerMode={!!isInViewAsBuyerMode}
                meetings={meetings}
                services={services}
                deleteMeeting={deleteMeeting}
                bookMeeting={doBookMeeting}
                getNextFreeTimesServices={getNextFreeTimes}
                getAvailableTimesServices={getTimes}
                confimMeeting={doConfirm}
            />
        </div>
    );
};

export default BuyerMeetings;
